import { Play, Clock, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to extract YouTube thumbnail from video URL
const getYouTubeThumbnail = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
    }
  }
  return null;
};

// Helper to format view count
const formatViewCount = (count) => {
  if (!count) return null;
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toLocaleString();
};

export const VideoCard = ({ video, index, seasonTitle, seasonColor }) => {
  const navigate = useNavigate();
  const [viewCount, setViewCount] = useState(null);
  
  // Fetch combined stats (YouTube + Facebook)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // First try to get cached combined views from video data
        if (video.primary_cached_views && video.primary_cached_views > 0) {
          // Sum primary + secondary sources
          let total = video.primary_cached_views;
          if (video.view_sources && video.view_sources.length > 0) {
            for (const source of video.view_sources) {
              total += source.cached_views || 0;
            }
          }
          setViewCount(total);
          return;
        }
        
        // Try metrics endpoint for combined views
        try {
          const metricsRes = await axios.get(`${API}/metrics/video/${video.id}`);
          if (metricsRes.data?.total_views && metricsRes.data.total_views > 0) {
            setViewCount(metricsRes.data.total_views);
            return;
          }
        } catch (e) {
          // Metrics endpoint failed, fallback to YouTube
        }
        
        // Final fallback: YouTube stats directly (legacy)
        if (video.platform === "youtube") {
          const ytRes = await axios.get(`${API}/youtube/stats/${video.id}`);
          if (ytRes.data?.view_count) {
            setViewCount(ytRes.data.view_count);
          }
        }
      } catch (error) {
        // Silently fail - views are optional
      }
    };
    
    if (video.id) {
      fetchStats();
    }
  }, [video.id, video.primary_cached_views, video.view_sources, video.platform]);
  
  // Auto-extract thumbnail from YouTube if not provided
  const getThumbnail = () => {
    if (video.thumbnail) return video.thumbnail;
    if (video.platform === "youtube") {
      const ytThumb = getYouTubeThumbnail(video.video_url);
      if (ytThumb) return ytThumb;
    }
    return "https://images.unsplash.com/photo-1684303041679-5897b02fbf18?crop=entropy&cs=srgb&fm=jpg&q=85";
  };
  
  const handleClick = () => {
    navigate(`/video/${video.slug || video.id}`);
  };

  // Determine if text should be white or black based on background color
  const getContrastColor = (hexColor) => {
    if (!hexColor) return "#000000";
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  };

  const badgeColor = seasonColor || "#FFFFFF";
  const textColor = getContrastColor(badgeColor);

  return (
    <div
      onClick={handleClick}
      className={`video-card relative overflow-hidden cursor-pointer bg-black card-jump hand-drawn-subtle animate-fade-in opacity-0 group`}
      style={{ animationDelay: `${index * 0.1}s`, animationFillMode: "forwards", borderWidth: '4px', borderColor: '#fff', borderStyle: 'solid' }}
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail */}
      <div className="aspect-video relative overflow-hidden bg-black">
        <img
          src={getThumbnail()}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        
        {/* Play Overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-[#E91E8C] flex items-center justify-center transform scale-50 group-hover:scale-100 transition-transform duration-300 border-2 border-white">
            <Play size={24} className="text-white fill-white ml-1" />
          </div>
        </div>

        {/* Duration Badge */}
        {video.duration && (
          <div className="absolute bottom-2 right-2 px-2 py-1 bg-black text-xs text-white flex items-center gap-1 font-bold border border-white">
            <Clock size={12} />
            {video.duration}
          </div>
        )}

        {/* Views Badge */}
        {viewCount && (
          <div className="absolute top-10 left-2 px-2 py-1 bg-black/80 text-xs text-white flex items-center gap-1 font-medium">
            <Eye size={12} />
            {formatViewCount(viewCount)}
          </div>
        )}

        {/* Season Badge */}
        <div 
          className="absolute top-2 left-2 px-2 py-1 text-xs uppercase font-black hand-drawn-subtle"
          style={{ 
            backgroundColor: badgeColor,
            color: textColor,
            borderWidth: '2px', 
            borderColor: textColor, 
            borderStyle: 'solid' 
          }}
        >
          {seasonTitle || video.platform}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 bg-white">
        <h3 className="font-bold text-black line-clamp-1 group-hover:text-[#E91E8C] transition-colors">
          {video.title}
        </h3>
        {video.description && (
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
            {video.description}
          </p>
        )}
      </div>
    </div>
  );
};
